/**
 * Sanitization utilities to prevent XSS and open redirect vulnerabilities.
 */

/** Dangerous URL protocols that must be rejected. */
const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript|file|blob):/i;

/** Regex for allowed redirect URL origins (io.net, io.solutions, antbit.io, localhost). */
const ALLOWED_ORIGIN_PATTERNS = [
  /^https?:\/\/localhost(:\d+)?$/,
  /^https:\/\/[a-zA-Z0-9-]+\.io\.net$/,
  /^https:\/\/[a-zA-Z0-9-]+\.staging\.webapp\.io\.solutions$/,
  /^https:\/\/[a-zA-Z0-9-]+\.webapp\.io\.solutions$/,
  /^https:\/\/[a-zA-Z0-9-]+\.antbit\.io$/,
  /^https:\/\/v2-webapp-ionet-.*-api-ionet\.vercel\.app$/
];

/**
 * Sanitizes a redirect URL to prevent open redirects and XSS via javascript: URLs.
 * Use when storing or using user-provided redirect URLs (e.g. from query params).
 */
export function sanitizeRedirectUrl(url: string | undefined | null): string {
  if (!url || typeof url !== "string") {
    return "/";
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return "/";
  }

  // 1. Block dangerous protocols (javascript:, data:, etc.)
  if (DANGEROUS_PROTOCOLS.test(trimmed)) {
    return "/";
  }

  // 2. Allow relative paths (must start with / but not //)
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return trimmed;
  }

  // 3. Validate absolute URLs
  try {
    const parsed = new URL(trimmed);
    const origin = parsed.origin;

    // Block non-http(s) protocols
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return "/";
    }

    // Allow if origin matches trusted patterns
    if (ALLOWED_ORIGIN_PATTERNS.some((pattern) => pattern.test(origin))) {
      return trimmed;
    }

    // Allow same-origin
    if (typeof window !== "undefined" && origin === window.location.origin) {
      return trimmed;
    }
  } catch {
    // URL parsing failed
    return "/";
  }

  return "/";
}
